import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useMutation } from 'react-query';
import PropTypes from 'prop-types';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { CssTextField } from 'pages/reference-implementation/components/select';
import { OrderPayload } from 'pages/user-preferences/store';
import HelpImg from 'pages/provisioning/_components/helpImg';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const RuleRequestors = ({ index }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [value, setValue] = React.useState([]);
    const [ccInputValue, setCCInputValue] = React.useState('');
    const [options, setOptions] = React.useState([]);
    const [searched, setSearched] = React.useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

    const employee2Api = useMutation(() => axios.get(`${endPoints.employee.listV2}?key=${ccInputValue}`));

    const setRequestors = (requestors, indexs) => {
        setOrder((prevOrder) => {
            const updatedRuleRows = prevOrder?.ruleRows?.map((rule, ruleIndex) => {
                if (ruleIndex === indexs) {
                    return {
                        ...rule,
                        value: requestors,
                    };
                }
                return rule;
            });

            const updatedOrder = {
                ...prevOrder,
                ruleRows: updatedRuleRows,
            };
            return updatedOrder;
        });
    };

    const resetOptions = () => {
        setOptions([]);
    };

    const fetch = React.useMemo(
        () =>
            throttle(() => {
                employee2Api.mutate();
            }, 1000),
        [ccInputValue]
    );

    const handleSearch = () => {
        if (ccInputValue === '') {
            resetOptions();
            setIsDropdownOpen(false);
        }
        if (ccInputValue !== '' && ccInputValue.length > 2) {
            setSearched(true);
            fetch();
        }
    };

    React.useEffect(() => {
        if (employee2Api?.data?.data) {
            const fetchedOptions = employee2Api?.data?.data?.map((app) => ({
                label: `${app.gpid} - ${app.name}`,
                gpid: `${app.gpid}`,
                name: `${app.name}`,
                email: `${app.email}`,
            }));

            // Merge fetched options with currently selected values
            const mergedOptions = [...new Map([...value, ...fetchedOptions]?.map((item) => [item?.gpid, item]))?.values()];

            setOptions(mergedOptions);
            setIsDropdownOpen(true);
        }
    }, [employee2Api?.data?.data, value]);

    const handleSelectionChange = (newValue) => {
        const uniqueValues = newValue?.filter((v, i, self) => self?.findIndex((item) => item?.gpid === v?.gpid) === i);
        setValue(uniqueValues);
        setRequestors(uniqueValues, index);
    };

    const getNoOptionText = () => {
        let text = '';
        if (searched === false) {
            text = 'Type & press enter to search by GPID, Firstname and Lastname';
        } else if (options?.length === 0) {
            text = 'No results found';
        }
        return text;
    };

    React.useEffect(() => {
        if (order?.ruleRows?.[index]?.value?.length === 0 && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.ruleRows, order?.isTouched, index]);

    const validateInput = () => {
        if ((isTouched && order?.isTouched && order?.ruleRows?.[index]?.value?.length === 0) || isTouched) {
            return 'validate';
        }
        return '';
    };

    return (
        <>
            <Box display="flex" alignItems="center" gap={1}>
                <Autocomplete
                    key="trusted-requestors"
                    size="small"
                    fullWidth
                    disablePortal
                    className={validateInput()}
                    disabled={employee2Api?.isLoading}
                    multiple
                    limitTags={1}
                    value={order?.ruleRows?.[index]?.value || value || null}
                    onChange={(_event, newValue) => {
                        handleSelectionChange(newValue);
                    }}
                    disableClearable
                    options={options}
                    disableCloseOnSelect
                    open={isDropdownOpen}
                    onOpen={() => setIsDropdownOpen(true)}
                    onClose={() => setIsDropdownOpen(false)}
                    getOptionLabel={(option) => option?.label}
                    isOptionEqualToValue={(option, selectedValue) => option?.gpid === selectedValue?.gpid}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                data-testid={`checkbox-option-${index}`}
                            />
                            {option.label}
                        </li>
                    )}
                    renderInput={(params) => (
                        <CssTextField
                            {...params}
                            label={order?.ruleRows?.[index]?.ruleType === null ? 'Select your Rule Type' : 'Type to search to select one or more Users *'}
                            value={ccInputValue}
                            data-testid={`rule-value-input-${index}`}
                            onChange={(e) => setCCInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            disabled={order?.ruleRows?.[index]?.ruleType === null}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {employee2Api?.isLoading && <CircularProgress color="inherit" size={16} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    noOptionsText={getNoOptionText()}
                />
                {order?.ruleRows?.[index]?.ruleType && (
                    <HelpImg title={<Box>A trusted requestor is a verified person or system authorized to securely request Order Auto Approvals.</Box>} />
                )}
            </Box>
        </>
    );
};

RuleRequestors.propTypes = {
    index: PropTypes.number.isRequired,
};

export default React.memo(RuleRequestors);
